import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TierBadge, STATUS_LABELS } from "@/components/Bits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STAGE_ORDER = [
  "new", "enriched", "qualified", "tier_suggested",
  "outreach_sent", "replied", "negotiating", "converted",
  "unsubscribed", "not_a_fit",
];

export default function Pipeline() {
  const [cols, setCols] = useState([]);
  const load = async () => {
    const res = await api.get("/pipeline");
    setCols(res.data.columns);
  };
  useEffect(() => { load(); }, []);

  const move = async (leadId, toStage) => {
    try {
      await api.post("/pipeline/move", { lead_id: leadId, to_stage: toStage });
      toast.success("Moved");
      load();
    } catch (e) {
      toast.error("Move failed");
    }
  };

  return (
    <div data-testid="pipeline-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="uppercase-tiny text-gray-500">Kanban</div>
          <h1 className="font-heading text-4xl tracking-tight mt-1">Pipeline</h1>
          <p className="text-sm text-gray-600 mt-2">Move leads through stages, from first import to converted partner.</p>
        </div>
      </div>

      <div className="flex overflow-x-auto gap-3 pb-4 snap-x no-scrollbar" data-testid="pipeline-columns">
        {cols.map((col) => (
          <div key={col.id} className="w-72 flex-shrink-0 bg-gray-50/60 border border-gray-200 rounded-sm p-3 snap-center" data-testid={`column-${col.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-700">{STATUS_LABELS[col.id] || col.id}</div>
              <span className="text-xs bg-white border border-gray-200 px-1.5 py-0.5 rounded-sm text-gray-600 font-mono" data-testid={`column-count-${col.id}`}>{col.count}</span>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
              {col.items.map((lead) => (
                <div key={lead.lead_id} className="bg-white border border-gray-200 p-3 rounded-sm hover:-translate-y-0.5 hover:shadow-sm transition-transform duration-200"
                  data-testid={`kanban-card-${lead.lead_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <Link to={`/leads/${lead.lead_id}`} className="text-sm font-medium hover:text-[#1B4332] leading-tight">
                      {lead.business_name}
                    </Link>
                    <TierBadge tier={lead.suggested_tier} />
                  </div>
                  {lead.category && <div className="text-[11px] text-gray-500 capitalize">{lead.category}</div>}
                  {lead.location && <div className="text-[11px] text-gray-500">{lead.location}</div>}
                  <Select value={col.id} onValueChange={(v) => move(lead.lead_id, v)}>
                    <SelectTrigger className="mt-2 h-7 text-xs rounded-sm" data-testid={`move-select-${lead.lead_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {col.items.length === 0 && (
                <div className="text-xs text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-sm">
                  Empty
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
